import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Stack, 
  Typography, 
  TextField, 
  Button, 
  Alert,
  IconButton
} from '@mui/material';
import { PersonSearchOutlined, SearchOutlined, Close as CloseIcon } from '@mui/icons-material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { useLookupRegistrationsQuery } from '../../../services/RegistrationService';
import type { RegistrationType } from '../../../services/RegistrationService/types/registrationTypes';
import { PageContainer, PageCard, CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import RegistrationLookupResultsGrid from '../../encounter/components/grids/RegistrationLookupResultsGrid';

const PatientLookupContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const { handleCenterChange } = useCenterSelection();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);

  // Use RTK Query to lookup registrations
  const {
    data: registrations,
    isLoading,
    error,
  } = useLookupRegistrationsQuery(
    { 
      company, 
      center, 
      searchTerm 
    },
    {
      skip: !shouldSearch || !company || !center || !searchTerm,
    }
  );

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setShouldSearch(true);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setShouldSearch(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectRegistration = (registration: RegistrationType) => {
    // The RegistrationLookupResultsGrid handles navigation
  };

  const canSearch = Boolean(company && center);

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{ minHeight: '600px', minWidth: '1000px', maxWidth: '1400px', width: '100%' }}>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>
                <PersonSearchOutlined sx={{ color: '#3b82f6', fontSize: 28 }} />
                <Box>
                  <Typography variant="h4" component="h1" fontWeight={600} color="#1e293b">
                    Patient Lookup
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff', minHeight: '500px' }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />
            </Box>
            <Stack spacing={3}>
              {/* Search Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                  Search Patients
                </Typography>
                
                {canSearch ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      autoFocus
                      label="Search Patient (MRN or Name)"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter MRN or patient name"
                      disabled={isLoading}
                      sx={{ flex: 1, maxWidth: 400 }}
                      InputProps={{
                        endAdornment: searchTerm && (
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{ color: '#64748b' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )
                      }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<SearchOutlined />}
                      onClick={handleSearch}
                      disabled={!searchTerm.trim() || isLoading}
                      sx={{
                        backgroundColor: '#162346',
                        '&:hover': { backgroundColor: '#1a2a52' },
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Search
                    </Button>
                  </Stack>
                ) : (
                  <Alert severity="warning">
                    Please select a company and center from the navigation to search for patients.
                  </Alert>
                )}
              </Box>

              {/* Error Display */}
              {error && (
                <Alert severity="error">
                  Error searching patients. Please try again.
                </Alert>
              )}

              {/* Results Section */}
              {shouldSearch && !isLoading && !error && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                    Search Results
                  </Typography>
                  
                  {registrations && registrations.length > 0 ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                        Found {registrations.length} patient{registrations.length !== 1 ? 's' : ''} matching "{searchTerm}"
                      </Typography>
                      <RegistrationLookupResultsGrid
                        registrations={registrations}
                        isLoading={isLoading}
                        onSelectRegistration={handleSelectRegistration}
                      />
                    </>
                  ) : (
                    <Alert severity="info">
                      No patients found matching "{searchTerm}". Try searching with a different term.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Loading State */}
              {isLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                  <Typography variant="body2" color="#64748b">
                    Searching patients...
                  </Typography>
                </Box>
              )}

              {/* Instructions - Only show when no search has been performed */}
              {!shouldSearch && canSearch && (
                <Box 
                  sx={{ 
                    backgroundColor: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: 1.5, 
                    p: 3,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body2" color="#64748b" sx={{ mb: 2, fontWeight: 500 }}>
                    Search Tips:
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="#64748b">
                      • Search by MRN (Medical Record Number)
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      • Search by patient first name, last name, or full name
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      • Search terms are case-insensitive
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      • Click on any result to view the patient's registration details
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

const PatientLookup: React.FC = () => {
  return (
    <AuthenticatedTemplate>
      <PatientLookupContent />
    </AuthenticatedTemplate>
  );
};

export default PatientLookup;
